import { Row, Table } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { DateRange as ReactDateRange } from 'react-date-range';
import { NumberRangeFilter } from 'src/components/Filters/NumberRangeFilter';
import { useContent } from 'src/contexts/ContentContext';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { ColumnFilterButton } from 'src/core/ui/ColumnFilterButton';
import { TooltipPopover } from 'src/core/ui/TooltipPopover';
import { CrossIcon } from 'src/svgs/Viagogo';
import { PosManagedMarketplace } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COMP_LISTING_MODE_TO_CID,
  OUTLIER_MODE_TO_CID,
  STANDARD_DEVIATIONS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { getIsInternationalEvent } from 'src/utils/eventWithDataUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { FilterOperationType, Marketplace } from 'src/WebApiController';

import * as styles from './FilterComponents.css';
import { getCurrencySymbol } from './filterFunctions';

interface FilterUIProps<TValue = any> {
  value: TValue;
  onChange: (value: TValue) => void;
  onClear: () => void;
  options?: Record<string, string>;
  columnId: string;
  table: Table<any>;
}

/**
 * Number range filter UI - min/max inputs in a popover
 */
export const NumberRangeFilterUI = ({
  value = [undefined, undefined],
  onChange,
  onClear,
}: FilterUIProps<[number | undefined, number | undefined]>) => {
  const isActive = value[0] != null || value[1] != null;

  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <NumberRangeFilter
        filterValue={{
          min: value[0],
          max: value[1],
          operation: FilterOperationType.Between,
        }}
        setFilterValue={(v) => {
          if (v == null) {
            onChange([undefined, undefined]);
            return;
          }
          onChange([v.min, v.max]);
        }}
      />
    </TooltipPopover>
  );
};

/**
 * Multi-select filter UI - dropdown with checkboxes
 */
export const MultiSelectFilterUI = ({
  value = [],
  onChange,
  options = {},
}: FilterUIProps<string[]>) => {
  const isActive = value.length > 0;
  const isSearchable = Object.keys(options).length > 10;

  return (
    <PosMultiSelect
      values={value}
      onChange={({ newValues }) => onChange(newValues)}
      onClick={(e) => e.stopPropagation()}
      enableEmptySelection
      valueOptionsContent={options}
      showSelectedOptionsFirst
      childrenAsTrigger
      placeholderText={ContentId.All}
      searchable={isSearchable}
    >
      <ColumnFilterButton isActive={isActive} />
    </PosMultiSelect>
  );
};

/**
 * Text search filter UI - simple text input with debounce
 */
export const TextSearchFilterUI = ({
  value = '',
  onChange,
  onClear,
}: FilterUIProps<string>) => {
  const [localValue, setLocalValue] = useState(value);

  const clearText = useContent(ContentId.Clear);
  const searchText = useContent(ContentId.Search);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      onChange(localValue);
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]);

  const isActive = Boolean(value);

  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <Stack direction="column" gap="m" style={{ minWidth: '200px' }}>
        <PosTextField
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={searchText}
          onClick={(e) => e.stopPropagation()}
        />

        <Stack direction="row" justifyContent="spaceBetween">
          <Button
            variant="text"
            onClick={() => {
              setLocalValue('');
              onClear();
            }}
            disabled={!isActive}
          >
            {clearText}
          </Button>
        </Stack>
      </Stack>
    </TooltipPopover>
  );
};

/**
 * Date range filter UI - single calendar with range selection
 */
export const DateRangeFilterUI = ({
  value = [undefined, undefined],
  onChange,
  onClear,
}: FilterUIProps<[Date | undefined, Date | undefined]>) => {
  const [minDate, maxDate] = value;
  const clearText = useContent(ContentId.Clear);
  const isActive = minDate != null || maxDate != null;

  // Create stable default dates that don't change on every render
  const defaultDate = useMemo(() => new Date(), []);

  const ranges = useMemo(
    () => [
      {
        startDate: minDate || defaultDate,
        endDate: maxDate || defaultDate,
        key: 'selection',
      },
    ],
    [minDate, maxDate, defaultDate]
  );
  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <Stack direction="column" gap="m">
        <div className={styles.customDateRangeContainer}>
          <ReactDateRange
            className={styles.dateRangePicker}
            ranges={ranges}
            onChange={(ranges) => {
              const selection = ranges.selection;
              if (selection) {
                onChange([selection.startDate, selection.endDate]);
              }
            }}
            locale={getLocaleFromLanguageOrCurrent()}
            showDateDisplay={false}
            direction="horizontal"
            rangeColors={[vars.color.backgroundHighlight]}
          />
        </div>
        <Stack direction="row" justifyContent="spaceBetween">
          <Button variant="text" onClick={() => onClear()} disabled={!isActive}>
            {clearText}
          </Button>
        </Stack>
      </Stack>
    </TooltipPopover>
  );
};

/**
 * Boolean filter UI - 3-way toggle (All/Yes/No)
 */
export const BooleanFilterUI = ({
  value,
  onChange,
}: FilterUIProps<boolean | null>) => {
  const allText = useContent(ContentId.All);
  const yesText = useContent(ContentId.Yes);
  const noText = useContent(ContentId.No);

  // Convert boolean to string for PosSelect
  const currentValue =
    value === null || value === undefined ? 'all' : String(value);

  const isActive = Boolean(value);

  const valueOptionsContent = {
    all: allText,
    true: yesText,
    false: noText,
  };

  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <PosSelect
        value={currentValue}
        onChange={(newValue) => {
          if (newValue === 'all') {
            onChange(null);
          } else {
            onChange(newValue === 'true');
          }
        }}
        valueOptionsContent={valueOptionsContent}
        enableEmptySelection={false}
      />
    </TooltipPopover>
  );
};

/**
 * Dual mode number filter UI - toggle between currency ($) and percentage (%) modes
 */
export const DualModeNumberFilterUI = ({
  value = { mode: undefined, min: undefined, max: undefined },
  onChange,
  onClear,
  table,
  columnId,
}: FilterUIProps<{
  mode?: 'absolute' | 'relative';
  min?: number;
  max?: number;
}>) => {
  const clearText = useContent(ContentId.Clear);

  // Default to absolute mode
  const selectedMode = value.mode || 'absolute';
  const prefix = selectedMode === 'absolute' ? '$' : '%';

  const isActive = value.min != null || value.max != null;

  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <Stack
        direction="column"
        gap="m"
        style={{ padding: '12px', minWidth: '240px' }}
      >
        {/* Mode selector */}
        <PosSelect
          value={selectedMode}
          onChange={(mode) =>
            onChange({
              mode: mode as 'absolute' | 'relative',
              min: value.min,
              max: value.max,
            })
          }
          valueOptionsContent={{
            absolute: 'Currency ($)',
            relative: 'Percentage (%)',
          }}
          enableEmptySelection={false}
        />

        {/* Min/Max inputs side-by-side */}
        <Stack direction="row" gap="m">
          <PosTextField
            type="number"
            value={value.min ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ mode: selectedMode, min: v, max: value.max });
            }}
            placeholder="Min"
            prefixDisplay={<span>{prefix}</span>}
          />

          <PosTextField
            type="number"
            value={value.max ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ mode: selectedMode, min: value.min, max: v });
            }}
            placeholder="Max"
            prefixDisplay={<span>{prefix}</span>}
          />
        </Stack>

        <Stack direction="row" justifyContent="spaceBetween">
          <Button variant="text" onClick={() => onClear()} disabled={!isActive}>
            {clearText}
          </Button>
          <CrossIcon withHoverEffect />
        </Stack>
      </Stack>
    </TooltipPopover>
  );
};

/**
 * Money range filter UI - currency selector + amount inputs with currency symbol
 */
export const MoneyRangeFilterUI = ({
  value = { currency: undefined, min: undefined, max: undefined },
  onChange,
  onClear,
  table,
  columnId,
}: FilterUIProps<{ currency?: string; min?: number; max?: number }>) => {
  const clearText = useContent(ContentId.Clear);

  // Extract unique currencies from table
  const availableCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    table.getCoreRowModel().flatRows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const moneyValue = row.getValue(columnId);

        let currency: string | null = null;

        // Extract currency from UiMoney object
        if (
          moneyValue &&
          typeof moneyValue === 'object' &&
          'currency' in moneyValue
        ) {
          currency = (moneyValue as any).currency;
        } else if (typeof moneyValue === 'number') {
          // Plain number - get currency from row.original
          const listing = (row.original as any)?.listing;
          const sale = (row.original as any)?.sale;
          const purchase = row.original as any;
          currency = listing?.currency || sale?.currency || purchase?.currency;
        }

        if (currency) {
          currencies.add(currency);
        }
      }
    });
    return Array.from(currencies).sort();
  }, [table, columnId]);

  // Auto-initialize currency in filter state when currencies are detected
  useEffect(() => {
    if (!value.currency && availableCurrencies.length > 0) {
      onChange({
        currency: availableCurrencies[0],
        min: value.min,
        max: value.max,
      });
    }
  }, [availableCurrencies, value.currency, value.min, value.max, onChange]);

  const selectedCurrency = value.currency || 'USD';
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  const isActive = value.min != null || value.max != null;

  return (
    <TooltipPopover triggerContent={<ColumnFilterButton isActive={isActive} />}>
      <Stack
        direction="column"
        gap="m"
        style={{ padding: '12px', minWidth: '240px' }}
      >
        {/* Show currency selector only if multiple currencies exist */}
        {availableCurrencies.length > 1 && (
          <PosSelect
            value={selectedCurrency}
            onChange={(ccy) =>
              onChange({ currency: ccy, min: value.min, max: value.max })
            }
            valueOptionsContent={Object.fromEntries(
              availableCurrencies.map((c) => [
                c,
                `${c} (${getCurrencySymbol(c)})`,
              ])
            )}
            enableEmptySelection={false}
          />
        )}

        {/* Min/Max inputs side-by-side */}
        <Stack direction="row" gap="m">
          <PosTextField
            type="number"
            value={value.min ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ currency: selectedCurrency, min: v, max: value.max });
            }}
            placeholder="Min"
            prefixDisplay={<span>{currencySymbol}</span>}
          />

          <PosTextField
            type="number"
            value={value.max ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              onChange({ currency: selectedCurrency, min: value.min, max: v });
            }}
            placeholder="Max"
            prefixDisplay={<span>{currencySymbol}</span>}
          />
        </Stack>

        <Stack direction="row" justifyContent="spaceBetween">
          <Button variant="text" onClick={() => onClear()} disabled={!isActive}>
            {clearText}
          </Button>
        </Stack>
      </Stack>
    </TooltipPopover>
  );
};

/**
 * Extract unique values from table rows for multi-select filters
 */
export const extractUniqueValues = (
  rows: Row<any>[],
  columnId: string,
  uniqueValueVariant?:
    | 'seatingSection'
    | 'seatTraits'
    | 'userTags'
    | 'marketplaceBroadcast'
    | 'outlierMode'
    | 'outlierStdDev'
    | 'compListingMode',
  venue?: any
): Record<string, string> => {
  // Special extractor for seating sections
  if (uniqueValueVariant === 'seatingSection') {
    const uniqueValues = new Set<string>();
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const value = row.getValue(columnId);
        if (value) {
          const { seating } = value as { seating: { section: string } };
          if (seating?.section) {
            uniqueValues.add(seating.section);
          }
        }
      }
    });
    return Object.fromEntries(
      Array.from(uniqueValues).map((value) => [value, value])
    );
  }

  // Special extractor for seat traits (listing notes)
  if (uniqueValueVariant === 'seatTraits') {
    const uniqueNotes = new Map<
      number,
      { listingNoteId: number; listingNoteText: string }
    >();
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const value = row.getValue(columnId);
        if (value) {
          const listingNotes = value as Array<{
            listingNoteId: number;
            listingNoteText: string;
          }> | null;
          if (listingNotes) {
            listingNotes.forEach((note) => {
              if (!uniqueNotes.has(note.listingNoteId)) {
                uniqueNotes.set(note.listingNoteId, note);
              }
            });
          }
        }
      }
    });

    return Object.fromEntries(
      Array.from(uniqueNotes.values()).map((note) => [
        note.listingNoteId.toString(),
        note.listingNoteText,
      ])
    );
  }

  // Special extractor for outlier mode - maps enum values to display names
  if (uniqueValueVariant === 'outlierMode') {
    const uniqueModes = new Set<string>();
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.outMode) {
          uniqueModes.add(listing.outMode);
        }
      }
    });

    // Map enum values to display names using OUTLIER_MODE_TO_CID
    return Object.fromEntries(
      Array.from(uniqueModes).map((mode) => {
        const displayName = (OUTLIER_MODE_TO_CID as any)[mode] || mode;
        return [mode, displayName];
      })
    );
  }

  // Special extractor for comp listing mode
  if (uniqueValueVariant === 'compListingMode') {
    const uniqueModes = new Set<string>();
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.compMode) {
          uniqueModes.add(listing.compMode);
        }
      }
    });

    // Map enum values to display names using COMP_LISTING_MODE_TO_CID
    return Object.fromEntries(
      Array.from(uniqueModes).map((mode) => {
        const displayName = (COMP_LISTING_MODE_TO_CID as any)[mode] || mode;
        return [mode, displayName];
      })
    );
  }

  // Special extractor for outlier standard deviations
  if (uniqueValueVariant === 'outlierStdDev') {
    const uniqueStdDevs = new Set<string>();
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.outlStdDev != null) {
          uniqueStdDevs.add(String(listing.outlStdDev));
        }
      }
    });

    // Map numbers to display names using STANDARD_DEVIATIONS_TO_CID
    const result: Record<string, string> = {};
    uniqueStdDevs.forEach((stdDev) => {
      const displayName = STANDARD_DEVIATIONS_TO_CID[stdDev];
      if (displayName) {
        result[stdDev] = displayName;
      }
    });
    return result;
  }

  // Special extractor for marketplace broadcast status
  if (uniqueValueVariant === 'marketplaceBroadcast') {
    const uniqueMarketplaces = new Set<string>();

    // Use passed venue to check if international event
    const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);
    rows.forEach((row) => {
      if (row.getLeafRows().length === 0) {
        const listing = (row.original as any)?.listing;
        if (listing?.mkpListings) {
          listing.mkpListings
            .filter(
              (m: any) =>
                m.status === 'Listed' &&
                m.mkp &&
                !PosManagedMarketplace.includes(m.mkp) &&
                // Match cell rendering: only StubHub for international events
                (m.mkp === Marketplace.StubHub || !isInternationalEvent)
            )
            .forEach((m: any) => {
              uniqueMarketplaces.add(m.mkp);
            });
        }
      }
    });
    return Object.fromEntries(
      Array.from(uniqueMarketplaces).map((mkp) => [mkp, mkp])
    );
  }

  // Default extractor
  const uniqueValues = new Map<string, string>();

  rows.forEach((row) => {
    // Skip parent rows (only process leaf rows)
    if (row.getLeafRows().length === 0) {
      const value = row.getValue(columnId);
      if (value != null) {
        const strValue = String(value);
        if (strValue.trim()) {
          uniqueValues.set(strValue, strValue);
        }
      }
    }
  });

  return Object.fromEntries(uniqueValues);
};
